import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { openModalEvent } from '@nx-frontend/shared/ui/modal';
import { generalEnv } from '@nx-frontend/shared/env';
import { $app } from '@nx-frontend/shared/store/app';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $orgCoupons } from '@nx-frontend/shared/store/org-coupons';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import {
  BillingAddonCode,
  GetIsAddonWasPurshasedEarlyProps,
  PaywallPopupReason,
  PopupType,
} from '@nx-frontend/shared/types';
import { GetOpenPayWallPopupProps, GetOpenAddonProps } from '@nx-frontend/shared/types';
import { hasMaxSubscriptionPlan } from '@nx-frontend/shared/utils';

interface UseBuyAutomationAddonParams {
  getOpenPaywallPopup: (params: GetOpenPayWallPopupProps) => void;
  getOpenAddonPopup: (params: GetOpenAddonProps) => void;
  getIsAddonWasPurshasedEarly: (params: GetIsAddonWasPurshasedEarlyProps) => Promise<boolean>;
}

/**
 * Hook returns callback to open Automation addon popup
 * @returns
 */
export const useBuyAutomationAddon = (params: UseBuyAutomationAddonParams) => {
  const { getOpenPaywallPopup, getOpenAddonPopup, getIsAddonWasPurshasedEarly } = params;

  const [app, orgInfo, billingInfo, orgCoupons, currentOrganization] = useUnit([
    $app,
    $orgInfo,
    $billingInfo,
    $orgCoupons,
    $currentOrganization,
  ]);
  const [openModal] = useUnit([openModalEvent]);
  const t = useTranslations('features');

  const openAddonPupup = useCallback(async () => {
    const isMaxSubscriptionPlan = hasMaxSubscriptionPlan(orgInfo, billingInfo, orgCoupons);
    const isAddonWasPurshasedEarlyOrMaxPlan = !isMaxSubscriptionPlan
      ? await getIsAddonWasPurshasedEarly({
          gatewayUrl: generalEnv.gatewayUrl,
          subscriptionId: billingInfo.subscriptionId,
          addon: BillingAddonCode.AUTOMATION,
          sessionId: app.sessionId,
        })
      : true;
    if (!isAddonWasPurshasedEarlyOrMaxPlan) {
      getOpenPaywallPopup({
        openModal,
        payload: {
          payloadType: PopupType.PAYWALL,
          mode: 'upgrade',
          title: t('paywall.title_upgrade_now'),
          reason: PaywallPopupReason.AUTOMATION,
          orgId: currentOrganization.globalId,
        },
      });
    } else {
      getOpenAddonPopup({
        openModal,
        payload: {
          payloadType: PopupType.ADDON,
          code: BillingAddonCode.AUTOMATION,
        },
      });
    }
  }, [
    app.sessionId,
    billingInfo,
    currentOrganization.globalId,
    openModal,
    orgCoupons,
    orgInfo,
    getOpenPaywallPopup,
    getOpenAddonPopup,
    getIsAddonWasPurshasedEarly,
    t,
  ]);

  return openAddonPupup;
};
